import { useContext } from 'react'
import { SearchFormProps } from '@awazevr/design-system.compositions.search-form'
import {
  DataLayerEventType,
  DataLayerFactory,
  TmsContext,
} from '@awazevr/mfe-common.contexts.tms'
import { newrelicErrorHandler } from 'src/utils/errorHandler/newrelicErrorHandler'

export const useSearchSelectionEvent = (): {
  handleAutoSuggestStart: () => void
  handleDestinationSelection: SearchFormProps['onDestinationSelectionEvent']
  handleDateSelection: SearchFormProps['onDateSelectionEvent']
  handleGuestSelection: SearchFormProps['onGuestSelectionEvent']
  handleSearchSelectionStart: SearchFormProps['onSearchSelectionStart']
  handleSearchSelectionClose: SearchFormProps['onSearchSelectionClose']
} => {
  const tms = useContext(TmsContext)

  const handleDestinationSelection: SearchFormProps['onDestinationSelectionEvent'] =
    (payload) => {
      const { source, searchTerm, autoSuggestMeta } = payload
      if (!source) {
        return
      }

      new DataLayerFactory()
        .getEvent(DataLayerEventType.SearchSelectionMade)
        .update(
          tms,
          {
            searchOptionKey: 'destination',
            searchOptionValue: source,
            searchTerm,
          },
          newrelicErrorHandler,
        )

      if (autoSuggestMeta) {
        new DataLayerFactory()
          .getEvent(DataLayerEventType.AutoSuggestClick)
          .update(
            tms,
            {
              itemType: autoSuggestMeta.itemType,
              itemName: autoSuggestMeta.itemName,
              position: autoSuggestMeta.position,
              searchTerm,
            },
            newrelicErrorHandler,
          )
      }
    }

  const handleAutoSuggestStart = () => {
    tms.updateDataLayer('link', 'auto_suggest_start')
  }

  const handleDateSelection: SearchFormProps['onDateSelectionEvent'] = (
    payload,
  ) => {
    const { field } = payload
    const action = (payload as { action?: string }).action

    if (!field) {
      return
    }

    if (field === 'date' && action === 'clear') {
      tms.updateDataLayer('link', 'search_clear_cal')
      return
    }

    new DataLayerFactory()
      .getEvent(DataLayerEventType.SearchSelectionMade)
      .update(
        tms,
        {
          searchOptionKey: 'date',
          searchOptionValue: field,
        },
        newrelicErrorHandler,
      )
  }

  const handleGuestSelection: SearchFormProps['onGuestSelectionEvent'] = (
    payload,
  ) => {
    const { field } = payload
    if (!field) {
      return
    }

    new DataLayerFactory()
      .getEvent(DataLayerEventType.SearchSelectionMade)
      .update(
        tms,
        {
          searchOptionKey: 'guest',
          searchOptionValue: field,
        },
        newrelicErrorHandler,
      )
  }

  const handleSearchSelectionStart: SearchFormProps['onSearchSelectionStart'] =
    (payload) => {
      const { searchOptionKey } = payload
      if (!searchOptionKey) {
        return
      }

      new DataLayerFactory()
        .getEvent(DataLayerEventType.SearchSelectionStart)
        .update(
          tms,
          {
            searchOptionKey,
          },
          newrelicErrorHandler,
        )
    }

  const handleSearchSelectionClose: SearchFormProps['onSearchSelectionClose'] =
    (payload) => {
      const { searchOptionKey } = payload
      if (!searchOptionKey) {
        return
      }
      new DataLayerFactory()
        .getEvent(DataLayerEventType.SearchSelectionClose)
        .update(
          tms,
          {
            searchOptionKey,
          },
          newrelicErrorHandler,
        )
    }

  return {
    handleAutoSuggestStart,
    handleDestinationSelection,
    handleDateSelection,
    handleGuestSelection,
    handleSearchSelectionStart,
    handleSearchSelectionClose,
  }
}
